import {
  atomWithStorage,
  unstable_NO_STORAGE_VALUE as NO_STORAGE_VALUE,
} from 'jotai/utils';
import {
  isValidRecorderPreferenceValue,
  RecorderPreferenceKey,
  RecorderPreferencesMigrationResult,
  recorderPreferenceKeys,
  recorderPreferenceLegacyKeys,
  recorderPreferencesVersion,
} from '../../recorder-preferences';

type PreferenceNormalizer<Value> = (value: unknown) => Value | undefined;

export function atomWithRecorderPreference<Value>(
  key: RecorderPreferenceKey,
  initialValue: Value,
  normalize?: PreferenceNormalizer<Value>,
) {
  const storage = {
    getItem: () => {
      const stored = window.electron.recorderPreferences.get(key);
      if (stored === undefined) {
        return NO_STORAGE_VALUE;
      }

      const normalized = normalize ? normalize(stored) : (stored as Value);
      if (normalized === undefined) {
        window.electron.recorderPreferences.delete(key);
        return NO_STORAGE_VALUE;
      }

      if (normalize && JSON.stringify(normalized) !== JSON.stringify(stored)) {
        window.electron.recorderPreferences.set(key, normalized);
      }

      return normalized;
    },
    setItem: (_storageKey: string, value: Value) => {
      window.electron.recorderPreferences.set(key, value);
    },
    removeItem: () => {
      window.electron.recorderPreferences.delete(key);
    },
  };

  return atomWithStorage<Value>(key, initialValue, storage);
}

function parseStoredValue(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

export function migrateLegacyRecorderPreferences(
  storage: Storage,
): RecorderPreferencesMigrationResult {
  const candidates: Partial<Record<RecorderPreferenceKey, unknown>> = {};
  const sourceKeys = new Map<RecorderPreferenceKey, string[]>();

  Object.values(recorderPreferenceKeys).forEach((key) => {
    const legacyKeys = recorderPreferenceLegacyKeys[key] ?? [key];
    const candidate = legacyKeys
      .map((legacyKey) => {
        const rawValue = storage.getItem(legacyKey);
        return rawValue === null ? undefined : parseStoredValue(rawValue);
      })
      .find((value) => isValidRecorderPreferenceValue(key, value));

    if (candidate !== undefined) {
      candidates[key] = candidate;
      sourceKeys.set(key, [...legacyKeys]);
    }
  });

  const result = window.electron.recorderPreferences.migrate(candidates) as
    | RecorderPreferencesMigrationResult
    | undefined;

  const safeResult: RecorderPreferencesMigrationResult = result ?? {
    version: recorderPreferencesVersion,
    migratedKeys: [],
    existingKeys: [],
    rejectedKeys: Object.keys(candidates),
  };

  [...safeResult.migratedKeys, ...safeResult.existingKeys].forEach((key) => {
    sourceKeys.get(key)?.forEach((sourceKey) => {
      storage.removeItem(sourceKey);
    });
  });

  return safeResult;
}
